import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

// Eastern (trading-day) date as YYYY-MM-DD, DST-correct. Used to scope a daily
// reset so a toggle set "today" doesn't carry into the next trading day.
const easternDay = (): string => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/**
 * useState backed by localStorage so the value survives page reloads and
 * navigation (component remounts) — e.g. an "ignore Bloomberg pending" toggle
 * that should stay set until the operator clears it, not reset every page load.
 *
 * `daily` scopes it to the Eastern trading day: a stored value from a previous
 * day reads back as `initial` (so the skip resets each morning). Custom
 * (de)serializers let it hold non-JSON-native values like a Set.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
  serialize: (v: T) => string = JSON.stringify,
  deserialize: (s: string) => T = JSON.parse,
  daily = false,
): [T, Dispatch<SetStateAction<T>>] {
  const read = (): T => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return initial;
      if (daily) {
        const sep = raw.indexOf("|");           // envelope "YYYY-MM-DD|<serialized>"
        if (sep < 0 || raw.slice(0, sep) !== easternDay()) return initial;
        return deserialize(raw.slice(sep + 1));
      }
      return deserialize(raw);
    } catch {
      return initial;
    }
  };
  const [value, setValue] = useState<T>(read);
  const serRef = useRef(serialize);
  serRef.current = serialize;
  useEffect(() => {
    try {
      const body = serRef.current(value);
      localStorage.setItem(key, daily ? `${easternDay()}|${body}` : body);
    } catch {
      /* storage unavailable / quota — non-fatal */
    }
  }, [key, value, daily]);
  return [value, setValue];
}

// Helpers for a Set<string> persisted as a JSON array.
export const setSerialize = (s: Set<string>): string => JSON.stringify([...s]);
export const setDeserialize = (raw: string): Set<string> => new Set(JSON.parse(raw) as string[]);
